import { useEffect, useMemo, useRef, useState } from "react";
import { fetchAgent247Events } from "../utils/api";
import { countUnreadAiAssistantEvents } from "../utils/aiAssistantReadState";
import { resolveAgent247OrganizationId } from "../utils/resolveAgent247OrganizationId";

const DEV_AI_ASSISTANT_ORG_ID = "695efe91312e4c3878fcf24a";
const isDevAiAssistantEnv = process.env.NODE_ENV !== "production";
const POLL_INTERVAL_MS = 25000;

/**
 * Resolves the "24/7 Inquiry Call Assist" unread badge count for the current
 * user/location, polling the same endpoint the Dashboard uses and staying in
 * sync with it via the "aiAssistantSeenUpdated" event + localStorage writes
 * (see utils/aiAssistantReadState.js). Used by Sidebar to show a live count
 * next to "AI Call Assist" that decrements as events are opened/read.
 */
export function useAiAssistantUnreadCount(activeLocation, currentUser) {
  const [events, setEvents] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const eventsRef = useRef([]);
  const orgRef = useRef("");

  const organizationId = useMemo(() => {
    const resolved = resolveAgent247OrganizationId(activeLocation, currentUser);
    if (resolved) return resolved;
    if (isDevAiAssistantEnv) return DEV_AI_ASSISTANT_ORG_ID;
    return "";
  }, [activeLocation, currentUser]);

  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  useEffect(() => {
    orgRef.current = organizationId;
  }, [organizationId]);

  useEffect(() => {
    setUnreadCount(countUnreadAiAssistantEvents(events, organizationId));
  }, [events, organizationId]);

  useEffect(() => {
    const onSeenUpdated = (ev) => {
      const oid = String(ev.detail?.organizationId || "");
      if (oid !== orgRef.current) return;
      setUnreadCount(countUnreadAiAssistantEvents(eventsRef.current, oid));
    };
    window.addEventListener("aiAssistantSeenUpdated", onSeenUpdated);
    return () => window.removeEventListener("aiAssistantSeenUpdated", onSeenUpdated);
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      const prefix = "aiAssistantSeenEvents:";
      if (!e.key || !e.key.startsWith(prefix)) return;
      const oid = e.key.slice(prefix.length);
      if (oid !== orgRef.current) return;
      setUnreadCount(countUnreadAiAssistantEvents(eventsRef.current, oid));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (!organizationId) {
      setEvents([]);
      return undefined;
    }
    let active = true;
    const poll = async () => {
      try {
        const rows = await fetchAgent247Events(organizationId, 150);
        if (!active) return;
        setEvents(Array.isArray(rows) ? rows : []);
      } catch {
        if (active) setEvents([]);
      }
    };
    void poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [organizationId]);

  return unreadCount;
}

export default useAiAssistantUnreadCount;
